import React, { useState, useEffect } from 'react';
import { useNotification } from './NotificationSystem';
import { API_URL } from '../config';
import './AdminDashboard.css';

function AdminDashboard({ token, onClose }) {
  const { showSuccess, showError, confirm } = useNotification();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('stats');
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'stats') {
        const response = await fetch(`${API_URL}/api/admin/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } else if (activeTab === 'users') {
        const response = await fetch(`${API_URL}/api/admin/users`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setUsers(data);
        }
      } else if (activeTab === 'settings') {
        const response = await fetch(`${API_URL}/api/admin/settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setSettings(data);
        }
      }
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId, username) => {
    const confirmed = await confirm(
      `Are you sure you want to delete user "${username}"? This action cannot be undone.`,
      'Delete User'
    );
    if (!confirmed) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        showSuccess('User deleted successfully');
        fetchData();
      } else {
        const error = await response.json();
        showError(`Failed to delete user: ${error.error}${error.details ? '\n' + error.details : ''}`);
        console.error('Delete user error:', error);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      showError('Failed to delete user: ' + error.message);
    }
  };

  const handleResetPassword = async (userId, username) => {
    if (!newPassword || newPassword.length < 6) {
      showError('Password must be at least 6 characters');
      return;
    }

    const confirmed = await confirm(`Reset password for user "${username}"?`, 'Reset Password');
    if (!confirmed) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ newPassword })
      });

      if (response.ok) {
        showSuccess('Password reset successfully');
        setSelectedUser(null);
        setNewPassword('');
      } else {
        const error = await response.json();
        showError(error.error || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      showError('Failed to reset password');
    }
  };

  const handleToggleRegistrations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/settings`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          registrations_enabled: !settings.registrations_enabled 
        })
      });

      if (response.ok) {
        const updated = await response.json();
        setSettings(updated);
      } else {
        showError('Failed to update settings');
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      showError('Failed to update settings');
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatUptime = (seconds) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${days}d ${hours}h ${minutes}m`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  return (
    <div className="admin-dashboard-backdrop" onClick={onClose}>
      <div className="admin-dashboard-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-dashboard-header">
          <h1>Admin Dashboard</h1>
          <button className="admin-close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="admin-dashboard-tabs">
          <button 
            className={`admin-tab ${activeTab === 'stats' ? 'active' : ''}`}
            onClick={() => setActiveTab('stats')}
          >
            Statistics
          </button>
          <button 
            className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            Users
          </button>
          <button 
            className={`admin-tab ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            Settings
          </button>
        </div>

        <div className="admin-dashboard-content">
          {loading ? (
            <div className="admin-loading">Loading...</div>
          ) : (
            <>
              {activeTab === 'stats' && stats && (
                <div className="admin-stats">
                  <div className="stats-grid">
                    <div className="stat-card">
                      <div className="stat-icon">👥</div>
                      <div className="stat-info">
                        <div className="stat-value">{stats.users.total}</div>
                        <div className="stat-label">Total Users</div>
                        <div className="stat-sub">{stats.users.online} online</div>
                      </div>
                    </div>

                    <div className="stat-card">
                      <div className="stat-icon">🎮</div>
                      <div className="stat-info">
                        <div className="stat-value">{stats.servers}</div>
                        <div className="stat-label">Servers</div>
                      </div>
                    </div>

                    <div className="stat-card">
                      <div className="stat-icon">💬</div>
                      <div className="stat-info">
                        <div className="stat-value">{stats.messages.toLocaleString()}</div>
                        <div className="stat-label">Messages</div>
                      </div>
                    </div>

                    <div className="stat-card">
                      <div className="stat-icon">🎤</div>
                      <div className="stat-info">
                        <div className="stat-value">{stats.users.inVoice}</div>
                        <div className="stat-label">In Voice</div>
                      </div>
                    </div>
                  </div>

                  <div className="system-info">
                    <h2>System Resources</h2>
                    <div className="resource-grid">
                      <div className="resource-item">
                        <strong>Memory Usage</strong>
                        <div className="resource-bar">
                          <div 
                            className="resource-fill" 
                            style={{ width: `${stats.system.memory.percentUsed}%` }}
                          />
                        </div>
                        <div className="resource-text">
                          {formatBytes(stats.system.memory.used)} / {formatBytes(stats.system.memory.total)} 
                          ({stats.system.memory.percentUsed}%)
                        </div>
                      </div>

                      <div className="resource-item">
                        <strong>CPU Load</strong>
                        <div className="resource-text">
                          {stats.system.cpu.load} ({stats.system.cpu.cores} cores)
                        </div>
                      </div>

                      <div className="resource-item">
                        <strong>Uptime</strong>
                        <div className="resource-text">{formatUptime(stats.system.uptime)}</div>
                      </div>

                      <div className="resource-item">
                        <strong>Platform</strong>
                        <div className="resource-text">
                          {stats.system.platform} - Node {stats.system.nodeVersion}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'users' && (
                <div className="admin-users">
                  <div className="users-list">
                    {users.map(user => (
                      <div key={user.id} className="user-row">
                        <div className="user-info">
                          <div className="user-avatar">
                            {user.avatar_url ? (
                              <img src={`${API_URL}${user.avatar_url}`} alt={user.username} />
                            ) : (
                              user.username[0].toUpperCase()
                            )}
                          </div>
                          <div className="user-details">
                            <div className="user-name">{user.username}</div>
                            <div className="user-email">{user.email}</div>
                            <div className="user-meta">
                              Joined: {formatDate(user.created_at)} | 
                              Status: <span className={`status-${user.status}`}>{user.status}</span>
                            </div>
                          </div>
                        </div>
                        <div className="user-actions">
                          <button 
                            className="btn-reset-password"
                            onClick={() => setSelectedUser(user)}
                          >
                            Reset Password
                          </button>
                          <button 
                            className="btn-delete-user"
                            onClick={() => handleDeleteUser(user.id, user.username)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {selectedUser && (
                    <div className="password-reset-modal">
                      <div className="password-reset-box">
                        <h3>Reset Password for {selectedUser.username}</h3>
                        <input
                          type="password"
                          placeholder="New password (min 6 characters)"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                        />
                        <div className="modal-actions">
                          <button 
                            onClick={() => handleResetPassword(selectedUser.id, selectedUser.username)}
                          >
                            Reset Password
                          </button>
                          <button onClick={() => {
                            setSelectedUser(null);
                            setNewPassword('');
                          }}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'settings' && settings && (
                <div className="admin-settings">
                  <div className="setting-row">
                    <div className="setting-info">
                      <strong>Allow User Registrations</strong>
                      <p>Control whether new users can create accounts</p>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={settings.registrations_enabled}
                        onChange={handleToggleRegistrations}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
